'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import en from '@/messages/en.json';
import ar from '@/messages/ar.json';
import hi from '@/messages/hi.json';
import bn from '@/messages/bn.json';

const LOCALE_STORAGE_KEY = 'erp_locale';
const LOCALES = ['en', 'ar', 'hi', 'bn'] as const;
export type Locale = (typeof LOCALES)[number];

function isLocale(s: string): s is Locale {
  return LOCALES.includes(s as Locale);
}

const allMessages: Record<Locale, Record<string, unknown>> = {
  en: en as Record<string, unknown>,
  ar: ar as Record<string, unknown>,
  hi: hi as Record<string, unknown>,
  bn: bn as Record<string, unknown>,
};

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  locales: readonly Locale[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isLocale(stored)) setLocaleState(stored);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    if (typeof window !== 'undefined') localStorage.setItem(LOCALE_STORAGE_KEY, l);
  }, []);

  const value: LocaleContextValue = { locale, setLocale, locales: LOCALES };
  const messages = allMessages[locale];

  if (!mounted) {
    return (
      <LocaleContext.Provider value={value}>
        <div className="flex min-h-screen items-center justify-center text-slate-500">Loading…</div>
      </LocaleContext.Provider>
    );
  }

  return (
    <LocaleContext.Provider value={value}>
      <NextIntlClientProvider locale={locale} messages={messages}>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
